import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';

import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import { resolveErrorMessage } from '@app/utils/errors';
import { formatDateTime } from '@app/utils/format';

import {
  deletePromotedFile,
  getManagedFileWithEtag,
  listPromotedFiles,
  updatePromotedFile,
  type OrgApiRequester,
} from './outputExportTransport';
import { type UploadedFileRecord } from './chatArtifactUtils';

type PickerTab = 'upload' | 'promoted';

interface RenameDraft {
  name: string;
  filename: string;
}

interface ComposerAttachmentPickerProps {
  disabled?: boolean;
  requestOrgApi: OrgApiRequester;
  onSelectLocalFiles: (files: File[]) => void;
  onSelectPromotedFile: (file: UploadedFileRecord) => void;
  triggerClassName?: string;
  triggerContent: ReactNode;
  triggerAriaLabel: string;
}

const resolveDisplayName = (file: UploadedFileRecord): string =>
  (file.name ?? file.filename ?? file.id ?? 'Promoted file').trim() || 'Promoted file';

const resolveFileName = (file: UploadedFileRecord): string =>
  (file.filename ?? file.name ?? file.id ?? 'file').trim() || 'file';

const formatFileSize = (sizeBytes?: number): string | null => {
  if (typeof sizeBytes !== 'number' || !Number.isFinite(sizeBytes) || sizeBytes < 0) {
    return null;
  }
  return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
};

const ComposerAttachmentPicker = ({
  disabled = false,
  requestOrgApi,
  onSelectLocalFiles,
  onSelectPromotedFile,
  triggerClassName,
  triggerContent,
  triggerAriaLabel,
}: ComposerAttachmentPickerProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PickerTab>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [files, setFiles] = useState<UploadedFileRecord[]>([]);
  const [renameFileId, setRenameFileId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState<RenameDraft>({ name: '', filename: '' });
  const [pendingDeleteFile, setPendingDeleteFile] = useState<UploadedFileRecord | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refreshPromotedFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await listPromotedFiles(requestOrgApi);
      setFiles(items);
    } catch (pickerError) {
      setError(pickerError instanceof Error && pickerError.message ? pickerError.message : 'Failed to load promoted files.');
    } finally {
      setLoading(false);
    }
  }, [requestOrgApi]);

  useEffect(() => {
    if (!open || activeTab !== 'promoted') {
      return;
    }
    void refreshPromotedFiles();
  }, [activeTab, open, refreshPromotedFiles]);

  const visibleFiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return files;
    }
    return files.filter((file) => {
      const haystack = [file.name, file.filename, file.description, file.mimeType]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .join('\n')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [files, search]);

  const handleLocalSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files ?? []);
      if (selected.length === 0) {
        return;
      }
      onSelectLocalFiles(selected);
      event.target.value = '';
      setOpen(false);
      setActiveTab('upload');
    },
    [onSelectLocalFiles],
  );

  const startRename = useCallback((file: UploadedFileRecord) => {
    setRenameFileId(file.id ?? null);
    setRenameDraft({
      name: resolveDisplayName(file),
      filename: resolveFileName(file),
    });
    setError(null);
  }, []);

  const cancelRename = useCallback(() => {
    setRenameFileId(null);
    setRenameDraft({ name: '', filename: '' });
  }, []);

  const saveRename = useCallback(
    async (fileId: string) => {
      setLoading(true);
      setError(null);
      try {
        const current = await getManagedFileWithEtag(requestOrgApi, fileId);
        const etag = current.etag?.trim();
        if (!etag) {
          throw new Error('Managed file is missing an ETag required for rename.');
        }
        const updated = await updatePromotedFile(requestOrgApi, fileId, etag, {
          name: renameDraft.name.trim() || undefined,
          filename: renameDraft.filename.trim() || undefined,
        });
        setFiles((previous) => previous.map((file) => (file.id === fileId ? updated.file : file)));
        cancelRename();
      } catch (renameError) {
        setError(renameError instanceof Error && renameError.message ? renameError.message : 'Failed to rename promoted file.');
      } finally {
        setLoading(false);
      }
    },
    [cancelRename, renameDraft.filename, renameDraft.name, requestOrgApi],
  );

  const confirmDelete = useCallback(
    async () => {
      const file = pendingDeleteFile;
      const fileId = file?.id?.trim();
      if (!file || !fileId || deletingFileId) {
        return;
      }
      const label = resolveDisplayName(file);
      setDeletingFileId(fileId);
      setError(null);
      try {
        const current = await getManagedFileWithEtag(requestOrgApi, fileId);
        const etag = current.etag?.trim();
        if (!etag) {
          throw new Error('Managed file is missing an ETag required for deletion.');
        }
        await deletePromotedFile(requestOrgApi, fileId, etag);
        setFiles((previous) => previous.filter((entry) => entry.id !== fileId));
        if (renameFileId === fileId) {
          cancelRename();
        }
        setPendingDeleteFile(null);
        setDeleteToast({ tone: 'success', message: `${label} deleted successfully.` });
      } catch (deleteError) {
        const message = await resolveErrorMessage(deleteError, 'Failed to delete promoted file.');
        setError(message);
        setDeleteToast({ tone: 'error', message });
      } finally {
        setDeletingFileId(null);
      }
    },
    [cancelRename, deletingFileId, pendingDeleteFile, renameFileId, requestOrgApi],
  );

  return (
    <div className="composer-attachment-picker">
      <input ref={fileInputRef} type="file" multiple hidden onChange={handleLocalSelect} />
      <button
        type="button"
        className={triggerClassName}
        aria-label={triggerAriaLabel}
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        disabled={disabled}
      >
        {triggerContent}
      </button>
      {open && (
        <div className="composer-attachment-picker__panel" role="dialog" aria-label="Attachment picker">
          <div className="composer-attachment-picker__tabs" role="tablist" aria-label="Attachment sources">
            <button
              type="button"
              className={`composer-attachment-picker__tab${activeTab === 'upload' ? ' composer-attachment-picker__tab--active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'upload'}
              onClick={() => setActiveTab('upload')}
            >
              Upload files
            </button>
            <button
              type="button"
              className={`composer-attachment-picker__tab${activeTab === 'promoted' ? ' composer-attachment-picker__tab--active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'promoted'}
              onClick={() => setActiveTab('promoted')}
            >
              Promoted files
            </button>
          </div>

          {activeTab === 'upload' ? (
            <div className="composer-attachment-picker__body">
              <p className="composer-attachment-picker__hint">
                Choose one or more local files to upload into the current thread.
              </p>
              <button
                type="button"
                className="composer-attachment-picker__action"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                Select local files
              </button>
            </div>
          ) : (
            <div className="composer-attachment-picker__body">
              <div className="composer-attachment-picker__toolbar">
                <input
                  type="search"
                  className="composer-attachment-picker__search"
                  placeholder="Search promoted files"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <button
                  type="button"
                  className="composer-attachment-picker__refresh"
                  onClick={() => void refreshPromotedFiles()}
                  disabled={loading}
                >
                  Refresh
                </button>
              </div>
              {error && <div className="composer-attachment-picker__error">{error}</div>}
              {loading && files.length === 0 ? <div className="composer-attachment-picker__hint">Loading promoted files…</div> : null}
              {!loading && visibleFiles.length === 0 ? (
                <div className="composer-attachment-picker__hint">No promoted files available.</div>
              ) : (
                <div className="composer-attachment-picker__list">
                  {visibleFiles.map((file) => {
                    const isRenaming = renameFileId === file.id;
                    const fileName = resolveFileName(file);
                    const sizeLabel = formatFileSize(file.sizeBytes);
                    return (
                      <div key={file.id ?? fileName} className="composer-attachment-picker__item">
                        {isRenaming ? (
                          <div className="composer-attachment-picker__rename">
                            <input
                              type="text"
                              value={renameDraft.name}
                              onChange={(event) => setRenameDraft((previous) => ({ ...previous, name: event.target.value }))}
                              placeholder="Display name"
                            />
                            <input
                              type="text"
                              value={renameDraft.filename}
                              onChange={(event) => setRenameDraft((previous) => ({ ...previous, filename: event.target.value }))}
                              placeholder="Stored file name"
                            />
                            <div className="composer-attachment-picker__item-actions">
                              <button type="button" onClick={() => void saveRename(file.id ?? '')} disabled={loading}>
                                Save
                              </button>
                              <button type="button" onClick={cancelRename} disabled={loading}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="composer-attachment-picker__item-meta">
                              <strong>{resolveDisplayName(file)}</strong>
                              <span>{fileName}</span>
                              <span>
                                {[file.mimeType, sizeLabel, formatDateTime(file.updatedAt ?? file.createdAt ?? null)]
                                  .filter((value): value is string => Boolean(value))
                                  .join(' · ')}
                              </span>
                            </div>
                            <div className="composer-attachment-picker__item-actions">
                              <button
                                type="button"
                                onClick={() => {
                                  onSelectPromotedFile(file);
                                  setOpen(false);
                                  setActiveTab('upload');
                                }}
                              >
                                Attach
                              </button>
                              <button type="button" onClick={() => startRename(file)}>
                                Rename
                              </button>
                              <button type="button" onClick={() => setPendingDeleteFile(file)} disabled={loading || deletingFileId != null}>
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <DeleteConfirmationModal
        open={pendingDeleteFile != null}
        title="Delete promoted file?"
        description={`Delete ${pendingDeleteFile ? `"${resolveDisplayName(pendingDeleteFile)}"` : 'this promoted file'}? This cannot be undone.`}
        deleting={deletingFileId != null}
        onCancel={() => setPendingDeleteFile(null)}
        onConfirm={() => void confirmDelete()}
      />
      {deleteToast && (
        <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />
      )}
    </div>
  );
};

export default ComposerAttachmentPicker;
